/**
 * What goes on a badge: who, from the shared guest list or from typed lines. Pure.
 */
import type { Guest } from "@/lib/workflow/types";

export interface BadgeEntry {
  name: string;
  organisation?: string;
  role?: string;
  /** Guest id, when the badge belongs to a guest in the project. Only guests get a signed QR pass. */
  id?: string;
}

export type BadgeWho = "coming" | "not-declined" | "all";

export const BADGE_WHO_LABEL: Record<BadgeWho, string> = {
  coming: "Confirmed and attended guests",
  "not-declined": "Everyone except declined",
  all: "Every guest",
};

export function badgesFromGuests(guests: readonly Guest[], who: BadgeWho): BadgeEntry[] {
  return guests
    .filter((g) => who === "all" || (who === "not-declined" ? g.status !== "declined" : g.status === "confirmed" || g.status === "attended"))
    .map((g) => ({ name: g.name, organisation: g.organisation, role: g.role, id: g.id }));
}

/** One badge per line: `Name | Organisation | Role` (a tab also separates). Blank lines are ignored. */
export function parseTypedBadges(text: string): BadgeEntry[] {
  const out: BadgeEntry[] = [];
  for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
    const parts = raw.split(/\s*[|\t]\s*/).map((p) => p.trim());
    if (!parts[0]) continue;
    out.push({ name: parts[0].slice(0, 120), organisation: parts[1]?.slice(0, 120) || undefined, role: parts[2]?.slice(0, 120) || undefined });
  }
  return out;
}

export const MAX_BADGES = 2000;

export const badgeFileName = (layoutId: string) => `name-badges-${layoutId.toLowerCase()}.pdf`;
